import {
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellEditingStartedEvent,
  BodyScrollEvent,
  ColumnResizedEvent,
  NavigateToNextCellParams,
  CsvExportParams,
  ExcelExportParams,
  ExcelCell,
  AgGridEvent,
  ProcessCellForExportParams,
  GridReadyEvent,
  DisplayedColumnsChangedEvent,
  CellPosition,
  GridApi,
  IMenuActionParams,
  Column,
} from '@ag-grid-community/core';

import type { CellKeyDownEvent, NewColumnsLoadedEvent, RowDataUpdatedEvent } from '@ag-grid-community/core';
import { clamp, isEmpty, isNil, isNumber, isString } from 'lodash';
import moment from 'moment';
import DataGrid, { DataGridProps } from './DataGrid';
import { Renderer } from './Renderer';
import textWidth from 'text-width';
import { worklistStatusMap } from 'src/components/WorklistIcon/WorklistIcon';
import { FilterModelType } from 'src/components/Sidenav/SideNav.slice';
import { patchExcelImageAnchors } from './patchExcelImageAnchors';

export const MOVE_DIRECTION_LOOKUP = {
  // gets row, column change based on arrow direction
  ['ArrowUp']: [-1, 0],
  ['ArrowDown']: [1, 0],
  ['ArrowLeft']: [0, -1],
  ['ArrowRight']: [0, 1],
  ['w']: [-1, 0],
  ['s']: [1, 0],
  ['a']: [0, -1],
  ['d']: [0, 1],
  ['Enter']: [1, 0],
};

type PrependRow = {
  cells: ExcelCell[];
  height?: number;
};

export function onCellClicked(this: DataGrid, event: CellClickedEvent): void {
  if (this.props.extraAgGridProps?.suppressRowClickSelection != true) {
    event.node.setSelected(true);
  }

  if (this.props.onCellClicked && !this.props.getAlternateContextMenu) {
    this.props.onCellClicked(event);
  } else if (this.props.onCellClicked && this.props.getAlternateContextMenu) {
    this.props.onCellClicked(event);
    this.noRenderState.leftOrRightClick = 'left';
    this.showWorklist(event);
  }
}
function getFinalColumnWidth(column: Column, padding = 50) {
  const contentWidth = column.getActualWidth();
  const headerName = column.getColDef().headerName || '';
  const headerWidth = textWidth(headerName, { font: 'Roboto', size: 12 }) + padding;
  return Math.max(contentWidth, headerWidth);
}
function autoResizeColumns(gridApi: GridApi, key: 'single' | 'all', params?: IMenuActionParams) {
  if (key === 'all') {
    gridApi.autoSizeAllColumns();
    const columnLimits = gridApi.getAllGridColumns().map((column) => ({
      key: column.getColId(),
      minWidth: getFinalColumnWidth(column),
    }));
    gridApi.sizeColumnsToFit({ columnLimits });
  } else if (key === 'single' && params?.column) {
    const column = params.column;
    const colId = column.getColId();
    gridApi.autoSizeColumns([colId]);

    const finalColumnWidth = getFinalColumnWidth(column);
    params.api.setColumnWidths([{ key: colId, newWidth: finalColumnWidth }]);
  }
}
export function getMainMenuItems(this: DataGrid) {
  return [
    'pinSubMenu', // Submenu for pinning. Always shown.
    {
      name: 'Autosize This Column',
      action: (params: IMenuActionParams) => {
        if (this.noRenderState.gridApi) {
          autoResizeColumns(this.noRenderState.gridApi, 'single', params);
        }
      },
    },
    // Auto-size all columns. Custom function as built-in doesn't work correctly
    {
      name: 'Autosize All Columns',
      action: () => {
        if (this.noRenderState.gridApi) {
          autoResizeColumns(this.noRenderState.gridApi, 'all');
        }
      },
    },
    'resetColumns', // Reset column details. Always shown.
    'expandAll', // Expand all groups. Only shown if grouping by at least one column.
    'contractAll', // Contract all groups. Only shown if grouping by at least one column.
  ];
}
export function onCellEditingStopped(this: DataGrid, event: CellEditingStoppedEvent): void {
  if (this.props.onCellEditingStopped) {
    this.props.onCellEditingStopped(event);
  }
}
export function onCellEditingStarted(this: DataGrid, event: CellEditingStartedEvent): void {
  if (this.props.onCellEditingStarted) {
    this.props.onCellEditingStarted(event);
  }
}
export function onBodyScroll(this: DataGrid, event: BodyScrollEvent): void {
  if (this.props.onBodyScroll) {
    this.props.onBodyScroll(event);
  }
}
export function onColumnResized(this: DataGrid, event: ColumnResizedEvent): void {
  if (event.finished && event.api) {
    event.api.redrawRows();
  }
}

export function getCellFromRowCol(
  this: DataGrid,
  rowChange: number,
  colChange: number,
  previousCellDef: CellPosition,
): CellPosition {
  if (!this.noRenderState.gridApi) {
    throw new Error('No grid apis found when moving, this shouldnt happen');
  }

  const colChangeLookup = {
    [-1]: this.noRenderState.gridApi.getDisplayedColBefore(previousCellDef.column),
    0: previousCellDef.column,
    1: this.noRenderState.gridApi.getDisplayedColAfter(previousCellDef.column),
  };

  // this gives us the last ag-grid row index (this returns rows.length)
  const lastRowIndex = this.noRenderState.gridApi.getDisplayedRowCount() - 1;

  // this can return null on the left/right side of the screen, but the type doesn't reflect that
  // fallback to the previous col if we get a null for the next one
  const nextColumn = colChangeLookup[colChange] ? colChangeLookup[colChange] : previousCellDef.column;
  const nextRow = clamp(previousCellDef.rowIndex + rowChange, 0, lastRowIndex);

  return {
    rowPinned: undefined,
    rowIndex: nextRow,
    column: nextColumn,
  };
}

export function onCellKeyDown(this: DataGrid, e: CellKeyDownEvent) {
  const event = e.event as unknown as React.KeyboardEvent;
  const isEditingCell = this.noRenderState.gridApi?.getEditingCells();
  if (event && MOVE_DIRECTION_LOOKUP[event.key] && this.noRenderState.gridApi && isEmpty(isEditingCell)) {
    e.event?.preventDefault();
    e.event?.stopPropagation();
    const key = event.key;
    const previousCell = this.noRenderState.gridApi.getFocusedCell();
    let [moveRow, moveColumn] = MOVE_DIRECTION_LOOKUP[key];
    if (!isNil(previousCell)) {
      if (event.shiftKey) {
        // flip everything when shift is held
        moveRow = moveRow * -1;
        moveColumn = moveColumn * -1;
      }

      const nextCellDef = this.getCellFromRowCol(moveRow, moveColumn, previousCell);
      this.noRenderState.gridApi.clearRangeSelection();
      this.noRenderState.gridApi.clearFocusedCell();
      this.noRenderState.gridApi.setFocusedCell(nextCellDef.rowIndex, nextCellDef.column, undefined);
      this.noRenderState.gridApi.addCellRange({
        rowStartIndex: nextCellDef.rowIndex,
        rowEndIndex: nextCellDef.rowIndex,
        columnStart: nextCellDef.column,
        columnEnd: nextCellDef.column,
      });
    }
  }
}

export function handleNavigateToNextCell(this: DataGrid, _params: NavigateToNextCellParams): CellPosition {
  // this is basically a well-typed noop()
  // all keyboard movement functionality is handled by onCellKeyDown()
  if (!this.noRenderState.gridApi) {
    throw new Error('No gridApi found when moving, this shouldnt happen');
  }
  return this.noRenderState.gridApi.getFocusedCell()!;
}
function getStatusForItem(itemId: string, itemsStatus: DataGridProps['itemsStatus']): string | null {
  if (!itemsStatus) {
    return 'Unknown Status'; // Handle case when itemsStatus is undefined
  }
  const { planning, worklist } = itemsStatus;

  // Check if the item exists in the planning array
  const planningItem = planning.find((item) => item.id === itemId);
  if (planningItem) {
    return planningItem.status; // Return the status from the planning array
  }

  // Check if the item exists in the worklist array
  const worklistItem = worklist.find((item) => item.product === itemId);
  if (worklistItem) {
    // Map the worklist type to a status using the worklistStatusMap
    return worklistStatusMap[worklistItem.type] || 'Unknown Worklist Status';
  }

  // If the item is not found in either, return a default value
  return null;
}

//#region IMAGE EXPORTING LOGIC / HELPERS

/** Holds images until export */
const excelImageCache: Record<string, string> = {};
/** Tracks failed image fetches, to put error text in the cells */
const excelImageFailed: Record<string, true> = {};
const IMG_RENDERERS = ['image', 'imageWithHover', 'imageRendererWithHover'];

/** Check if column is an image renderer */
function isImageRendererColumn(column: Column): boolean {
  const colDef: any = column.getColDef();
  const renderer = colDef?.renderer || colDef?.cellRenderer;
  return isString(renderer) && IMG_RENDERERS.includes(renderer);
}

/** Check if value is a valid url */
function isHttpUrl(value: unknown): value is string {
  return isString(value) && value.startsWith('http');
}

/** Define image type based on data URL */
function getImageTypeFromDataUrl(dataUrl: string): 'png' | 'jpg' {
  if (dataUrl.startsWith('data:image/png')) return 'png';
  return 'jpg'; // works for jpeg as well
}

/** Convert img url (cell value) to blob then dataUrl for ag-grid to accept */
async function urlToDataUrl(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
/** Gather & convert all the images in the export */
async function preloadImagesForExport(api: GridApi) {
  const displayedColumns = api.getAllDisplayedColumns();
  const imageColumns = displayedColumns.filter((col) => isImageRendererColumn(col));

  if (isEmpty(imageColumns)) return;

  const urls: string[] = [];

  api.forEachNodeAfterFilterAndSort((node) => {
    if (node.group || !node.data) return;

    imageColumns.forEach((col) => {
      const colDef: any = col.getColDef();
      const fieldKey = colDef?.field || col.getColId();
      const value = node.data[fieldKey];

      if (isHttpUrl(value) && !urls.includes(value)) {
        urls.push(value);
      }
    });
  });

  for (const url of urls) {
    if (excelImageCache[url]) continue;

    try {
      const dataUrl = await urlToDataUrl(url);
      excelImageCache[url] = dataUrl;
    } catch (e) {
      // no way to specifically catch CORS errors, because theres no response
      console.warn('[excel] CORS / fetch failed:', url, e);
      excelImageFailed[url] = true;
    }
  }
}
/** Get row indexes that contain images so we can make those rows taller */
function getExcelRowsWithImages(api: GridApi): number[] {
  const imageColumns = api.getAllDisplayedColumns().filter(isImageRendererColumn);
  const imageRowIndexes: number[] = [];

  let excelRowIndex = 7; // first row of actual data in the Excel sheet

  api.forEachNodeAfterFilterAndSort((node) => {
    if (node && !node.group && node.data) {
      for (const col of imageColumns) {
        const colDef: any = col.getColDef();
        const fieldKey = colDef?.field || col.getColId();
        const value = node.data[fieldKey];

        if (isHttpUrl(value) && excelImageCache[value] && !excelImageFailed[value]) {
          imageRowIndexes.push(excelRowIndex);
          break;
        }
      }
    }

    excelRowIndex += 1;
  });

  return imageRowIndexes;
}
//#endregion

export async function onExcelExport(this: DataGrid) {
  const exportOptions = this.props.exportOptions;
  const processCellOverride = exportOptions && exportOptions.processCellOverride;
  const showGroupTitlesExcel = exportOptions && exportOptions.showGroupTitlesExcel;
  const itemsStatus = this.props.itemsStatus;

  const api = this.noRenderState.gridApi;
  if (!api) return;

  // preload images once before export
  await preloadImagesForExport(api);

  const DEFAULT_ROW_HEIGHT = 16;

  /** rows that contain an image, to be used while calculating rowHeight */
  const rowsWithImages = getExcelRowsWithImages(api);
  const displayedColumnCount = api.getAllDisplayedColumns().length || 1;
  const mergeAcrossAll = Math.max(displayedColumnCount - 1, 0);

  const excelTitle = exportOptions?.excelTitle ?? 'S5 Excel Export';
  const excelScope = exportOptions?.excelScope ?? '';
  const excelFilters = exportOptions?.excelFilters ?? '';
  const excelExportSource = exportOptions?.excelExportSource ?? '';

  // getting column width for image columns to calculate the row height for excel export
  // so that the height matches the aspect ratio of the images and they are not stretched
  const imageColumn = api.getAllDisplayedColumns().find(isImageRendererColumn);
  const imageColumnWidth = imageColumn?.getActualWidth() ?? 70; // fallback
  const IMAGE_ASPECT_RATIO = 150 / 130;
  const IMAGE_ROW_HEIGHT = Math.round(imageColumnWidth * IMAGE_ASPECT_RATIO);
  const hasAnyImages = rowsWithImages.length > 0;

  let excelOptions: ExcelExportParams = {
    sheetName: 'S5 Assortment Export',
    headerRowHeight: 26,
    /** if images are present, all rows are taller to accommodate */
    rowHeight: () => (hasAnyImages ? IMAGE_ROW_HEIGHT : DEFAULT_ROW_HEIGHT),
    addImageToCell: (rowIndex, column, value) => {
      if (!isImageRendererColumn(column)) return undefined;
      if (!isHttpUrl(value)) return undefined;
      const cachedDataUrl = excelImageCache[value];
      if (!cachedDataUrl) return undefined;

      const imageType = getImageTypeFromDataUrl(cachedDataUrl);

      return {
        image: {
          id: `img-${rowIndex}-${column.getColId()}`,
          base64: cachedDataUrl,
          imageType,
          fitCell: true,
        },
        value: '',
      };
    },
    processCellCallback: function (params: ProcessCellForExportParams) {
      const { column, value, node } = params;
      const colId = column.getColId();
      let cellRenderer = column.getColDef()['renderer'] || column.getColDef()['cellRenderer'];

      if (isImageRendererColumn(column)) {
        if (!isHttpUrl(value)) return 'NO IMAGE';

        if (excelImageFailed[value]) {
          return 'ERROR';
        }

        return value; // addImageToCell handles the image after this
      }

      if (isNil(cellRenderer) && isNumber(value)) {
        cellRenderer = node ? node.data.formatter : undefined;
      }
      //convert to string status adorments column value when export excel
      if (cellRenderer === 'adornmentsGridRenderer') {
        const itemStatus = getStatusForItem(params.value, itemsStatus);
        return itemStatus;
      }

      const renderer = typeof cellRenderer === 'string' ? cellRenderer : '';
      const renderFn = renderer ? Renderer[renderer] : undefined;
      const isFirstColumn = colId === 'ag-Grid-AutoColumn';

      if (isFirstColumn && showGroupTitlesExcel) {
        // return the group name for all the group column cells if configured
        return node?.data.group[0];
      }

      // Handle group columns correctly for export when nested
      if (isFirstColumn && node?.group && node?.groupData) {
        // Only show the current level value
        return node.groupData[colId];
      }

      if (processCellOverride) {
        const possibleNewValue = processCellOverride(params);
        if (possibleNewValue) {
          return possibleNewValue;
        }
      }

      if (exportOptions && exportOptions.excelRendererObj && renderer !== '') {
        const excelFormat = exportOptions.excelRendererObj[renderer];
        if (excelFormat) {
          const columnDataType = column.getColDef().cellDataType === 'number';
          const isValueNumber =
            columnDataType &&
            (!isNumber(params.value) || value === 'NaN' || value === '-Infinity' || value === 'Infinity');
          return !isValueNumber ? value : null;
        }
      }

      return !isFirstColumn && renderFn ? renderFn(value) : value;
    },
  };

  const prependRows: PrependRow[] = [];

  prependRows.push({
    height: 30,
    cells: [
      {
        styleId: 'exportTitle',
        data: { type: 'String', value: excelTitle },
        mergeAcross: mergeAcrossAll,
      },
    ],
  },
    { // scope row
      height: 16,
      cells: [
        {
          styleId: 'scopeLabel',
          data: { type: 'String', value: 'Scope:' },
        },
        {
          styleId: 'scopeValue',
          data: { type: 'String', value: excelScope },
          mergeAcross: Math.max(displayedColumnCount - 2, 0),
        },
      ],
    },
    { // filter row
      height: 16,
      cells: [
        {
          styleId: 'scopeLabel',
          data: { type: 'String', value: 'Filters:' },
        },
        {
          styleId: 'scopeValue',
          data: { type: 'String', value: excelFilters },
          mergeAcross: Math.max(displayedColumnCount - 2, 0),
        },
      ],
    },
    { // export source row
      height: 16,
      cells: [
        {
          styleId: 's5ScopeLabel',
          data: { type: 'String', value: 'Exported from:' },
        },
        {
          styleId: 's5ScopeValue',
          data: { type: 'String', value: excelExportSource },
          mergeAcross: Math.max(displayedColumnCount - 2, 0),
        },
      ],
    }
  );
  const resolvedFileName = exportOptions?.fileName
    ? `${exportOptions.fileName} at ${moment().format('YYYY-MM-DD hh|mmA')}`
    : 'export.xlsx';

  excelOptions = {
    ...excelOptions,
    prependContent: prependRows,
    fileName: resolvedFileName,
  };


  if (rowsWithImages.length > 0) {
    const rawBlob = api.getDataAsExcel(excelOptions) as Blob;
    // Only run the patcher when there are images
    const patchedBlob = await patchExcelImageAnchors(rawBlob);
    // need to trigger download manually with the patched blob, as ag-grid doesn't provide a way to use the patched blob with their built in download
    const url = URL.createObjectURL(patchedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resolvedFileName;
    a.click();
    URL.revokeObjectURL(url);
  } else {
    api.exportDataAsExcel(excelOptions);
  }
}

export function onCsvExport(this: DataGrid) {
  const processCellOverride = this.props.exportOptions && this.props.exportOptions.processCellOverride;
  let csvOptions: CsvExportParams = {
    processCellCallback: function (params: ProcessCellForExportParams) {
      const { column, value } = params;
      const colId = column.getColId();
      const cellRenderer = column.getColDef()['renderer'] || column.getColDef()['cellRenderer'];
      const renderer = typeof cellRenderer === 'string' ? cellRenderer : '';
      const renderFn = renderer ? Renderer[renderer] : undefined;
      const isColorColumn = colId.indexOf('band_color') >= 0;

      // Change HEX color codes to N/A.
      if (isColorColumn) {
        return 'N/A';
      }

      if (processCellOverride) {
        const possibleNewValue = processCellOverride(params);
        if (possibleNewValue) {
          return possibleNewValue;
        }
      }
      return renderFn ? renderFn(value) : value;
    },
  };

  if (this.props.exportOptions) {
    const { customHeader, fileName } = this.props.exportOptions;
    csvOptions = {
      ...csvOptions,
      prependContent: customHeader && customHeader + '\n',
      fileName: fileName && fileName + ' at ' + moment().format('YYYY-MM-DD hh|mmA'),
    };
  }

  if (this.noRenderState.gridApi) {
    this.noRenderState.gridApi.exportDataAsCsv(csvOptions);
  }
}

export function onGridReady(this: DataGrid, event: GridReadyEvent) {
  this.noRenderState.gridApi = event.api;

  event.api.updateGridOptions({ columnDefs: this.processColDefs() });

  const { defaultShownValues } = this.noRenderState;
  if (!this.props.loaded) {
    event.api.showLoadingOverlay();
  }
  // when applicable apply default sorts/filters falling back to favorite values
  if (defaultShownValues) {
    event.api.setFilterModel(defaultShownValues);
  }
  if (!isEmpty(this.props.gridFilterModel)) {
    event.api.setFilterModel(this.props.gridFilterModel);
  }
  if (!isEmpty(this.props.gridSortModel)) {
    event.api.applyColumnState({ state: this.props.gridSortModel });
  }

  if (this.props.isPrintMode) {
    event.api.setDomLayout('print');
  }

  if (this.props.onGridReady) {
    this.props.onGridReady(event);
  }

  // at times even though the data prop has been modified and updated via gridApi
  // the grid will still not render any data so this forces it on grid ready to refresh
  // this should be called after `this.props.onGridReady` so that parent components have their
  // gridApi when data is set, which may be needed to calculated side effects
  event.api.updateGridOptions({ rowData: this.props.data });
}

export function onNewColumnsLoaded(this: DataGrid, event: NewColumnsLoadedEvent) {
  if (this.props.onNewColumnsLoaded) {
    this.props.onNewColumnsLoaded(event);
  }
}

export function onDisplayedColumnsChanged(this: DataGrid, event: DisplayedColumnsChangedEvent) {
  if (this.props.onDisplayedColumnsChanged) {
    this.props.onDisplayedColumnsChanged(event);
  }
}
/**
 * Merges the `newFilterModel` with the previous `oldFilterModel`,
 * preventing an empty array override for "set" filters.
 *
 *  - If `newFilterModel[colId]` is { filterType: 'set', values: [] }
 *    but `oldFilterModel[colId]` had a non-empty array, we restore the old array. ('keeping' the selection)
 *  - This effectively stops the forced emptying of the selection just
 *    because the data changed and the set filter didn’t find matching values.
 */
function mergeSetFilterModels(newFilterModel: FilterModelType, oldFilterModel?: FilterModelType) {
  // If one is missing, skip
  if (!oldFilterModel || !newFilterModel) {
    return newFilterModel;
  }

  // Clone so that we can modify it safely
  const mergedModel = { ...newFilterModel };

  // Iterate over all columns in the new filter model
  Object.keys(mergedModel).forEach((colId) => {
    const newFilter = mergedModel[colId];
    const oldFilter = oldFilterModel[colId];

    if (
      newFilter?.filterType === 'set' && // Must be a Set Filter
      Array.isArray(newFilter.values) &&
      newFilter.values.length === 0 && // It's trying to become empty b/c the data to the grid has changed
      oldFilter?.filterType === 'set' &&
      Array.isArray(oldFilter.values) &&
      oldFilter.values.length > 0 // We used to have a non-empty selection
    ) {
      // Reapply the old selection array
      mergedModel[colId] = {
        ...newFilter,
        values: oldFilter.values,
      };
    }
  });

  return mergedModel;
}

export function onFilterChanged(this: DataGrid, event: AgGridEvent) {
  const newFilterModel = event.api.getFilterModel();
  const oldFilterModel = this.props.gridFilterModel;

  // Merge the filters to 'keep' selections on data change (this was implemented with INT-4683 - filter selections weren't holding on flowStatus changes)
  const merged = mergeSetFilterModels(newFilterModel, oldFilterModel);

  if (this.props.storeGridFilterModel) {
    this.props.storeGridFilterModel(merged);
  }
}

export function onSortChanged(this: DataGrid, event: AgGridEvent) {
  const currentSortModel = event.api.getColumnState();
  if (this.props.storeGridSortModel) {
    this.props.storeGridSortModel(currentSortModel);
  }
}

export function onRowDataUpdated(this: DataGrid, event: RowDataUpdatedEvent) {
  if (!isEmpty(this.props.gridFilterModel)) {
    event.api.setFilterModel(this.props.gridFilterModel);
  }
  if (!isEmpty(this.props.gridSortModel)) {
    event.api.applyColumnState({ state: this.props.gridSortModel });
  }
  if (this.props.onRowDataUpdated) {
    this.props.onRowDataUpdated(event);
  }
}
